/**
 * Background Exception Logger
 * ---------------------------
 * Detects when the application (web or native via Capacitor) transitions to the
 * background and logs an exception/event. This helps diagnose mysterious errors
 * that only occur when the user backgrounds the app (e.g., interrupted streams,
 * paused audio recording, WebSocket disconnects, etc.).
 *
 * Usage: Call `setupBackgroundExceptionLogger()` once at app start. It will
 * register the necessary listeners and return a cleanup function that should be
 * invoked on teardown (e.g., in a React useEffect cleanup).
 */

/* eslint-disable no-console */

/**
 * Logs a debug event when app goes to background. Uses console.debug to avoid
 * cluttering the console with error-level messages for normal lifecycle events.
 * @param {string} message - Debug message
 * @param {object} [extra] - Additional context information
 */
export const logBackgroundEvent = (message, extra = {}) => {
  try {
    console.debug('[BackgroundEvent]', message, extra);
    // TODO: send to backend or analytics endpoint when available
  } catch (_) {
    // Prevent secondary failures
  }
};

/**
 * Sets up background detection listeners for both web and native contexts.
 * @returns {function} cleanup function to remove registered listeners
 */
export const setupBackgroundExceptionLogger = () => {
  // ---- Web: Page Visibility API ----
  const handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      logBackgroundEvent('App moved to background (visibilitychange)', {
        timestamp: new Date().toISOString(),
      });
    }
  };

  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // ---- Native (Capacitor): App State Change ----
  let appStateListener;
  (async () => {
    try {
      // Dynamically import to avoid adding @capacitor/app to web bundle if unused
      const { App } = await import('@capacitor/app');
      appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive === false) {
          logBackgroundEvent('App moved to background (Capacitor AppState)', {
            timestamp: new Date().toISOString(),
          });
        }
      });
    } catch (_err) {
      // Plugin not available (web build) – silently ignore
    }
  })();

  // ---- Cleanup ----
  return () => {
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (appStateListener && typeof appStateListener.remove === 'function') {
      appStateListener.remove();
    }
  };
};
